import { useCallback, useEffect, useState, useRef } from "react";
import { UploadCloud, FileText, Image as ImgIcon, Trash2, Tag, Calendar, FolderArchive, Search, X, ExternalLink, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api, fileToBase64 } from "@/lib/api";
import Paywall from "@/components/Paywall";

const categoryLabels = {
  ricevuta: "Ricevuta",
  referto_medico: "Referto medico",
  garanzia: "Garanzia",
  assicurazione: "Assicurazione",
  bolletta: "Bolletta",
  contratto: "Contratto",
  foto_famiglia: "Foto famiglia",
  documento_identita: "Documento ID",
  scuola: "Scuola",
  altro: "Altro",
};

const categoryColors = {
  ricevuta: "#F2CC8F",
  referto_medico: "#B5838D",
  garanzia: "#81B29A",
  assicurazione: "#3D5A80",
  bolletta: "#E07A5F",
  contratto: "#A8A29E",
  foto_famiglia: "#F28C71",
  documento_identita: "#3D5A80",
  scuola: "#81B29A",
  altro: "#78716C",
};

export default function VaultPage() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [opening, setOpening] = useState(false);
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [paywall, setPaywall] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Vault refresh failed:", err?.message);
      toast.error("Errore caricamento");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleFiles = useCallback(async (files) => {
    const fileList = files && files.length ? Array.from(files) : [];
    if (fileList.length === 0) {
      return;
    }
    setUploading(true);
    for (const file of fileList) {
      try {
        const b64 = await fileToBase64(file);
        toast.info(`AI sta analizzando ${file.name}...`);
        await api.post("/documents", {
          filename: file.name,
          file_b64: b64,
          mime_type: file.type || "application/octet-stream",
          user_note: "",
        });
        toast.success(`${file.name} archiviato`);
      } catch (e) {
        if (e?.response?.status === 402) {
          setPaywall({
            title: "Limite documenti raggiunto",
            message: e.response.data?.detail || "Hai raggiunto i 50 documenti del piano Free. Passa a Standard per archivio illimitato.",
          });
          break;
        }
        toast.error(`Errore su ${file.name}`);
      }
    }
    setUploading(false);
    refresh();
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il documento?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Eliminato");
    setSelected(null);
    refresh();
  };

  const openFile = async (doc) => {
    setOpening(true);
    try {
      const mime = (doc.mime_type || "").toLowerCase();
      const isHeic = mime === "image/heic" || mime === "image/heif";
      const suffix = isHeic ? "?preview=1" : "";
      const res = await api.get(`/documents/${doc.id}/file${suffix}`, { responseType: "blob" });
      const blobType = isHeic ? "image/jpeg" : (doc.mime_type || "application/octet-stream");
      const blob = new Blob([res.data], { type: blobType });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Impossibile aprire il file");
    } finally {
      setOpening(false);
    }
  };

  const saveRename = async () => {
    const title = renameValue.trim();
    if (!title || title === selected.title) { setRenaming(false); return; }
    try {
      const { data } = await api.patch(`/documents/${selected.id}`, { title });
      toast.success("Rinominato");
      setSelected({ ...selected, title: data.title });
      setRenaming(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rinomina");
    }
  };

  const categories = Array.from(new Set(docs.map(d => d.category)));
  const filtered = docs.filter(d => {
    if (filter !== "all" && d.category !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return d.title?.toLowerCase().includes(q) ||
        d.summary?.toLowerCase().includes(q) ||
        (d.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  return (
    <div className="px-5 md:px-10 py-8 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold">Memoria</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Vault Documenti</h1>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="vault-dropzone"
        className={`border-2 border-dashed rounded-3xl p-8 text-center cursor-pointer transition-all ${dragOver ? "border-brand bg-elevated" : "border-subtle bg-surface hover:bg-elevated"}`}
      >
        <UploadCloud className="w-10 h-10 mx-auto mb-3" style={{ color: dragOver ? "var(--brand)" : "var(--text-tertiary)" }} strokeWidth={1.4} />
        <div className="font-display text-lg font-medium">
          {uploading ? "Caricamento e analisi AI..." : "Trascina qui o clicca per caricare"}
        </div>
        <div className="text-sm text-tertiary-fg mt-1">PDF, immagini, scontrini · L&apos;AI classifica automaticamente</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,.pdf,.doc,.docx"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="vault-file-input"
        />
      </div>

      <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-1.5 rounded-full text-sm border whitespace-nowrap ${filter === "all" ? "bg-elevated border-brand text-primary-fg" : "border-subtle text-tertiary-fg"}`}
        >
          Tutti ({docs.length})
        </button>
        {categories.map(c => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-4 py-1.5 rounded-full text-sm border whitespace-nowrap ${filter === c ? "bg-elevated border-brand text-primary-fg" : "border-subtle text-tertiary-fg"}`}
          >
            {categoryLabels[c] || c}
          </button>
        ))}
      </div>

      <div className="relative mt-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-fg" />
        <input
          data-testid="vault-search"
          className="input-warm pl-10"
          placeholder="Cerca per titolo, tag, contenuto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
        {filtered.map((d, i) => (
          <button
            key={d.id}
            onClick={() => setSelected(d)}
            data-testid={`document-card-${i}`}
            className="bg-surface border border-subtle rounded-2xl p-4 text-left hover:bg-elevated transition-all hover:-translate-y-0.5 animate-fade-up"
            style={{ animationDelay: `${i * 30}ms` }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                style={{ backgroundColor: categoryColors[d.category] || "var(--bg-elevated)" }}
              >
                {d.mime_type?.startsWith("image/") ? <ImgIcon className="w-5 h-5 text-white" /> : <FileText className="w-5 h-5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{d.title || d.filename}</div>
                <div className="text-xs text-tertiary-fg mt-0.5">{categoryLabels[d.category] || d.category}</div>
              </div>
            </div>
            {d.summary && <p className="text-xs text-secondary-fg mt-3 line-clamp-2">{d.summary}</p>}
            {(d.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {d.tags.slice(0, 3).map(t => (
                  <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-elevated text-tertiary-fg border border-subtle">
                    {t}
                  </span>
                ))}
              </div>
            )}
            {d.expiry_date && (
              <div className="flex items-center gap-1 text-[11px] text-amber-warm mt-3">
                <Calendar className="w-3 h-3" /> Scade: {d.expiry_date}
              </div>
            )}
          </button>
        ))}
        {filtered.length === 0 && !uploading && (
          <div className="col-span-full text-center text-tertiary-fg py-10 text-sm">
            <FolderArchive className="w-10 h-10 mx-auto mb-2 opacity-40" />
            Nessun documento qui.
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-app/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div
            className="bg-surface border border-subtle rounded-3xl max-w-lg w-full max-h-[90vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="document-detail-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-tertiary-fg">{categoryLabels[selected.category]}</div>
                {renaming ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenaming(false); }}
                      className="input-warm text-xl font-semibold"
                      data-testid="document-rename-input"
                    />
                    <button onClick={saveRename} data-testid="document-rename-save" className="p-2 rounded-full hover:bg-elevated shrink-0">
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <h3 className="font-display text-2xl font-semibold truncate">{selected.title}</h3>
                    <button
                      onClick={() => { setRenameValue(selected.title || ""); setRenaming(true); }}
                      data-testid="document-rename-button"
                      className="p-1.5 rounded-full hover:bg-elevated transition shrink-0 text-tertiary-fg"
                      title="Rinomina"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => setSelected(null)} className="p-2 rounded-full hover:bg-elevated shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-secondary-fg leading-relaxed">{selected.summary}</p>
            {selected.expiry_date && (
              <div className="mt-4 flex items-center gap-2 text-amber-warm text-sm">
                <Calendar className="w-4 h-4" /> Scadenza: {selected.expiry_date}
              </div>
            )}
            {selected.amount && <div className="mt-2 text-sm">Importo: <span className="text-brand font-medium">{selected.amount}</span></div>}
            {selected.merchant && <div className="mt-1 text-sm text-secondary-fg">Esercente: {selected.merchant}</div>}
            {(selected.persons || []).length > 0 && (
              <div className="mt-2 text-sm text-secondary-fg">Persone: {selected.persons.join(", ")}</div>
            )}
            {(selected.tags || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {selected.tags.map(t => <span key={t} className="text-xs px-2 py-1 rounded-full bg-elevated border border-subtle"><Tag className="w-3 h-3 inline mr-1" />{t}</span>)}
              </div>
            )}
            <div className="mt-3 text-xs text-tertiary-fg">Caricato da {selected.uploader_name}</div>
            <button
              onClick={() => openFile(selected)}
              disabled={opening}
              data-testid="document-open-button"
              className="mt-6 w-full bg-brand text-white rounded-full py-2.5 flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
            >
              <ExternalLink className="w-4 h-4" /> {opening ? "Apertura..." : "Apri file"}
            </button>
            <button
              onClick={() => remove(selected.id)}
              data-testid="document-delete-button"
              className="mt-3 w-full bg-elevated text-rose-warm border border-subtle rounded-full py-2.5 flex items-center justify-center gap-2 hover:bg-subtle"
            >
              <Trash2 className="w-4 h-4" /> Elimina
            </button>
          </div>
        </div>
      )}
      <Paywall open={!!paywall} onClose={() => setPaywall(null)} title={paywall?.title} message={paywall?.message} />
    </div>
  );
}
